import { type ReactNode, useState } from "react";
import HelpOutlinedIcon from "@mui/icons-material/HelpOutlined";
import { Box, Chip, IconButton, Tooltip, Typography } from "@mui/material";

export const aovHelpContent = (
    <Box sx={{ p: 0.5, maxWidth: 320 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
            AOV — Средна стойност на поръчката
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
            Този показател проследява средната сума, която един клиент харчи при извършването на
            единична транзакция в обект.
        </Typography>
        <Typography variant="caption" color="text.secondary" component="div">
            Формула за изчисляване:
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
            AOV = Общи приходи ÷ Общ брой поръчки
        </Typography>
    </Box>
);

export const clvHelpContent = (
    <Box sx={{ p: 0.5, maxWidth: 320 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
            CLV — Жизнена стойност на клиента
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
            Прогнозната обща сума от приходи, която фирмата очаква да получи от един клиент за
            цялото време на пазаруването му.
        </Typography>
        <Typography variant="caption" color="text.secondary" component="div">
            Формула за изчисляване:
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
            CLV = AOV × Честота на покупките × Продължителност на клиентския живот
        </Typography>
    </Box>
);

export function MetricHelpIcon({ title, label }: { title: ReactNode; label: string }) {
    const [open, setOpen] = useState(false);

    return (
        <Tooltip
            open={open}
            onClose={() => setOpen(false)}
            title={title}
            arrow
            placement="top"
            disableFocusListener
            disableHoverListener
            disableTouchListener
            slotProps={{
                tooltip: {
                    sx: {
                        bgcolor: "background.paper",
                        color: "text.primary",
                        border: "1px solid",
                        borderColor: "divider",
                        boxShadow: 3,
                        p: 0.5,
                    },
                },
                arrow: {
                    sx: { color: "background.paper", "&::before": { border: "1px solid", borderColor: "divider" } },
                },
            }}
        >
            <IconButton
                size="small"
                aria-label={`Повече информация за ${label}`}
                onClick={() => setOpen((prev) => !prev)}
                sx={{ p: 0.35, color: "text.secondary" }}
            >
                <HelpOutlinedIcon sx={{ fontSize: 17 }} />
            </IconButton>
        </Tooltip>
    );
}

export function MetricChip({ code, value, help }: { code: string; value: string; help: ReactNode }) {
    return (
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
            <Chip size="small" label={`${code} ${value}`} />
            <MetricHelpIcon title={help} label={code} />
        </Box>
    );
}

export function ClvLabelWithHelp({ align = "center" }: { align?: "left" | "center" | "right" }) {
    return (
        <Box
            component="span"
            sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.25,
                justifyContent: align === "right" ? "flex-end" : align === "left" ? "flex-start" : "center",
            }}
        >
            CLV
            <MetricHelpIcon title={clvHelpContent} label="CLV" />
        </Box>
    );
}
